import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Smartphone, 
  Monitor, 
  Globe, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  Users,
  TrendingUp,
  Target,
  BarChart3,
  Shield,
  Settings,
  Play,
  Download,
  Star,
  Clock,
  DollarSign,
  Building2,
  Cpu,
  Database,
  Cloud,
  Lock,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  ExternalLink,
  Lightbulb,
  PieChart,
  Activity,
  Wifi,
  SmartphoneIcon,
  Tablet,
  Laptop,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import DigitalFirstBg from '../components/assets/digital.png';
import DigitalFirstBg2 from '../components/assets/digital-first.jpg';
import DigitalFirstBg3 from '../components/assets/digital-first1.jpg';
import CommonContactUs from './CommonContactUs';

const DigitalFirstPage = (props) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderImages = [DigitalFirstBg2, DigitalFirstBg, DigitalFirstBg3];

  const goToPrev = () => setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  const goToNext = () => setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));

  const services = [
    {
      icon: <Smartphone className="w-8 h-8 text-blue-600" />,
      title: "Mobile-First Development",
      description: "Build applications that prioritize mobile experience while maintaining desktop functionality.",
      features: ["Responsive design", "Progressive Web Apps", "Native app development", "Cross-platform solutions"]
    },
    {
      icon: <Globe className="w-8 h-8 text-green-600" />,
      title: "Digital Transformation",
      description: "Transform your business processes with modern digital solutions and workflows.",
      features: ["Process automation", "Digital workflows", "Legacy system modernization", "Change management"]
    },
    {
      icon: <Monitor className="w-8 h-8 text-purple-600" />,
      title: "Web Applications",
      description: "Modern web applications built with cutting-edge technologies and best practices.",
      features: ["Single Page Applications", "Real-time updates", "API integration", "Performance optimization"]
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-600" />,
      title: "Digital Acceleration",
      description: "Accelerate your digital initiatives with rapid development and deployment.",
      features: ["Agile development", "CI/CD pipelines", "Microservices architecture", "DevOps practices"]
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Increased Efficiency",
      description: "Streamline operations and reduce manual processes with digital solutions."
    },
    {
      icon: <Users className="w-8 h-8 text-green-600" />,
      title: "Better User Experience",
      description: "Provide seamless, intuitive experiences across all digital touchpoints."
    },
    {
      icon: <DollarSign className="w-8 h-8 text-purple-600" />,
      title: "Cost Reduction",
      description: "Reduce operational costs through automation and digital optimization."
    },
    {
      icon: <Target className="w-8 h-8 text-orange-600" />,
      title: "Competitive Advantage",
      description: "Stay ahead of the competition with modern digital capabilities."
    }
  ];

  const technologies = [
    {
      name: "React & React Native",
      description: "Modern frontend development for web and mobile applications",
      icon: <Monitor className="w-6 h-6" />
    },
    {
      name: "Node.js & Express",
      description: "Scalable backend development with JavaScript",
      icon: <Cpu className="w-6 h-6" />
    },
    {
      name: "MongoDB & PostgreSQL",
      description: "Flexible database solutions for modern applications",
      icon: <Database className="w-6 h-6" />
    },
    {
      name: "AWS & Azure",
      description: "Cloud infrastructure and deployment solutions",
      icon: <Cloud className="w-6 h-6" />
    },
    {
      name: "Docker & Kubernetes",
      description: "Containerization and orchestration for scalable deployments",
      icon: <Settings className="w-6 h-6" />
    },
    {
      name: "CI/CD Pipelines",
      description: "Automated testing and deployment workflows",
      icon: <Activity className="w-6 h-6" />
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Chief Nursing Officer",
      company: "Major Hospital Network",
      content: "TechMind transformed our approach to healthcare staffing. Their understanding of our specialized needs and ability to find qualified candidates quickly has been invaluable to our operations.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "CTO",
      company: "Innovation Labs",
      content: "TechMind didn't just fill positions—they understood our business challenges and found talent that could truly drive our digital transformation forward.",
      rating: 5
    },
    {
      name: "Alex Rivera",
      role: "Founder & CEO",
      company: "Future Tech",
      content: "As a fast-growing startup, finding the right talent quickly was make-or-break for us. TechMind understood our culture and technical needs perfectly, delivering exceptional candidates who have become core team members.",
      rating: 5
    }
  ];
   // Auto-advance slider every 4 seconds
      useEffect(() => {
        const interval = setInterval(() => {
          setCurrentSlide((prev) =>
            prev === sliderImages.length - 1 ? 0 : prev + 1
          );
        }, 8000);
        return () => clearInterval(interval);
      }, [sliderImages.length]);
      const scrollIntoView = () => {
        const element = document.getElementById("learn-more-digital");
        if (element) {
          const yOffset = -80; // adjust based on your header height
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      };

  return (
    <div className="min-h-screen">

      {/* Hero Section with Slider */}
      {props?.from==='Home' ?null:
      <section className="relative bg-gradient-auth h-[716px] text-white overflow-hidden pt-24">
        <div className="absolute inset-0 bg-black/20 z-10"></div>
        {/* Slider Images */}
        <div className="absolute inset-0 w-full h-full flex transition-all duration-700 z-0">
          {sliderImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt="Digital First Slide"
              className={`object-cover w-full h-full absolute top-0 left-0 transition-opacity duration-700 ${currentSlide === idx ? 'opacity-60 z-20' : 'opacity-0 z-0'}`}
              style={{ transition: 'opacity 0.7s' }}
            />
          ))}
        </div>
        {/* Slider Controls */}
        <div className="absolute left-0 right-0 flex justify-between items-center h-full px-4 z-30 pointer-events-none">
          <button
            onClick={goToPrev}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Previous Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={goToNext}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Next Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30" style={{zIndex:'42'}}>
          {sliderImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full ${currentSlide === idx ? 'bg-white' : 'bg-white/40'} border border-white transition-all`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-56 z-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Digital First
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Transform your business with modern digital solutions that prioritize user experience and innovation
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-blue-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Get Started
              </Link>
              <Link 
                // to="/services"
                to="#"
                onClick={scrollIntoView}
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-blue-600 transition-colors font-medium"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
     }

      {/* Benefits Section */}
      <section id='learn-more-digital' className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Digital First?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Embrace the future with digital-first strategies that drive growth and innovation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Digital First Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital solutions designed for the modern business landscape
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Modern Technologies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built with cutting-edge technologies for optimal performance and scalability
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-4">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{tech.name}</h3>
                </div>
                <p className="text-gray-600">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from clients who have transformed their businesses with our digital-first approach
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm d-flex justify-content-between flex-column"
              >
                {/* <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div> */}
                <div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Go Digital First?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Transform your business with modern digital solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-blue-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Start Your Digital Journey
              </Link>
              <Link 
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-blue-600 transition-colors font-medium"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}
      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default DigitalFirstPage; 